import { Fragment, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { Icon, type IconName } from "../../rtl/glyphs";
import { Button, type ButtonVariant } from "../Button/Button";
import { IconButton } from "../IconButton/IconButton";
import { Popover, type PopoverTriggerProps } from "../Popover/Popover";

export interface ActionMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: IconName;
  readonly onSelect?: () => void;
  readonly href?: string;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  /** Reason shown as tooltip (e.g. locked by plan). */
  readonly hint?: string;
  readonly testId?: string;
}

/** Items are grouped; a separator is drawn between groups. */
export type ActionMenuGroup = readonly ActionMenuItem[];

export interface ActionMenuProps {
  readonly label: string;
  /** Flat list or array of groups. */
  readonly items: readonly ActionMenuItem[] | readonly ActionMenuGroup[];
  readonly trigger?: (p: PopoverTriggerProps) => ReactNode;
  readonly icon?: IconName;
  readonly placement?: "start" | "end";
  readonly testId?: string;
}

function toGroups(items: ActionMenuProps["items"]): ActionMenuGroup[] {
  if (items.length > 0 && Array.isArray(items[0])) return items as ActionMenuGroup[];
  return [items as ActionMenuGroup];
}

/** Generic `⋯` menu: roving focus, typeahead, groups, danger/disabled/hint, sheet on phones. */
export function ActionMenu({
  label,
  items,
  trigger,
  icon = "more",
  placement,
  testId,
}: ActionMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const groups = toGroups(items).filter((g) => g.length > 0);
  const typed = useRef({ text: "", at: 0 });

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const els = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>("[role=menuitem]:not([aria-disabled=true])") ??
        [],
    );
    if (els.length === 0) return;
    const at = els.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === "ArrowDown") next = at < 0 ? 0 : (at + 1) % els.length;
    else if (e.key === "ArrowUp") next = at <= 0 ? els.length - 1 : at - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = els.length - 1;
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      const now = Date.now();
      typed.current.text = (now - typed.current.at < 700 ? typed.current.text : "") + e.key;
      typed.current.at = now;
      const q = typed.current.text.toLowerCase();
      next = els.findIndex((el) => el.textContent?.trim().toLowerCase().startsWith(q));
    }
    if (next < 0) return;
    e.preventDefault();
    els[next]?.focus();
  }

  return (
    <Popover
      label={label}
      role="menu"
      placement={placement}
      testId={testId}
      panelClassName="p-1"
      trigger={
        trigger ??
        ((p) => <IconButton {...p} label={label} icon={<Icon name={icon} />} variant="ghost" />)
      }
    >
      {(close) => (
        <div ref={listRef} onKeyDown={onKeyDown} className="flex flex-col">
          {groups.map((g, gi) => (
            <Fragment key={gi}>
              {gi > 0 && <hr role="separator" className="my-1 border-line" />}
              {g.map((it) => {
                const cls = cx(
                  "flex min-h-9 w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm",
                  "hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none",
                  "aria-disabled:cursor-not-allowed aria-disabled:opacity-60",
                  it.danger ? "text-danger-fg" : "text-fg",
                  "pointer-coarse:min-h-11",
                );
                const inner = (
                  <>
                    {it.icon && <Icon name={it.icon} size={18} />}
                    <span className="min-w-0 flex-1 truncate">{it.label}</span>
                  </>
                );
                return it.href && !it.disabled ? (
                  <a
                    key={it.id}
                    role="menuitem"
                    href={it.href}
                    title={it.hint}
                    data-testid={it.testId}
                    className={cls}
                    onClick={(e) => {
                      // `href` keeps open-in-new-tab working; `onSelect` (SPA navigation) wins on click.
                      if (it.onSelect) {
                        e.preventDefault();
                        it.onSelect();
                      }
                      close();
                    }}
                  >
                    {inner}
                  </a>
                ) : (
                  <button
                    key={it.id}
                    type="button"
                    role="menuitem"
                    title={it.hint}
                    aria-disabled={it.disabled || undefined}
                    data-testid={it.testId}
                    className={cls}
                    onClick={() => {
                      if (it.disabled) return;
                      it.onSelect?.();
                      close();
                    }}
                  >
                    {inner}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
      )}
    </Popover>
  );
}

export interface SplitButtonProps {
  readonly label: string;
  readonly onClick: () => void;
  readonly items: readonly ActionMenuItem[] | readonly ActionMenuGroup[];
  readonly variant?: ButtonVariant;
  readonly disabled?: boolean;
  readonly menuLabel?: string;
  readonly icon?: IconName;
  readonly testId?: string;
}

/** Primary action + chevron opening an `ActionMenu` (G1). */
export function SplitButton({
  label,
  onClick,
  items,
  variant = "primary",
  disabled,
  menuLabel,
  icon = "plus",
  testId,
}: SplitButtonProps) {
  const { t } = useI18n();
  const menu = menuLabel ?? t("ui.menu.more");
  return (
    <div className="inline-flex items-stretch" data-testid={testId}>
      <Button
        variant={variant}
        onClick={onClick}
        disabled={disabled}
        startIcon={<Icon name={icon} size={18} />}
        className="rounded-e-none"
      >
        {label}
      </Button>
      <ActionMenu
        label={menu}
        items={items}
        placement="end"
        trigger={(p) => (
          <button
            {...p}
            type="button"
            aria-label={menu}
            title={menu}
            disabled={disabled}
            className={cx(
              "inline-flex h-10 w-9 items-center justify-center rounded-e-md border-s border-on-action-primary/30",
              variant === "primary"
                ? "bg-action-primary text-on-action-primary hover:bg-action-primary-hover"
                : "border border-line-strong bg-action-secondary text-on-action-secondary",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:opacity-60",
              "pointer-coarse:min-w-11",
            )}
          >
            <Icon name="chevron-down" size={18} />
          </button>
        )}
      />
    </div>
  );
}
